import { type ReactNode, useCallback, useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const tooltipMaxWidth = 280;
const viewportPadding = 12;
const tooltipGap = 8;
const tooltipZIndex = 2147483647;

const triggerStyle = {
    display: "inline-flex",
    position: "relative",
    verticalAlign: "middle",
} as const;

const helpIconStyle = {
    alignItems: "center",
    border: "1px solid #8a8a8a",
    borderRadius: "50%",
    color: "#616161",
    cursor: "help",
    display: "inline-flex",
    fontSize: 11,
    fontWeight: 600,
    height: 18,
    justifyContent: "center",
    lineHeight: "18px",
    width: 18,
} as const;

type TooltipPosition = {
    left: number;
    top: number;
    transform: string;
};

type SettingsTooltipProps = {
    children: ReactNode;
    content: string;
    toggleOnClick?: boolean;
};

const getTooltipContainer = (trigger: HTMLElement | null): HTMLElement | null => {
    if (!trigger) return null;

    return trigger.closest<HTMLElement>(
        ".pf-field-modal__panel, .pf-safe-modal__panel, .pf-settings-editor, .Polaris-Page, #root"
    ) ?? document.body;
};

export const SettingsTooltip = ({ children, content, toggleOnClick = false }: SettingsTooltipProps) => {
    const tooltipId = useId();
    const triggerRef = useRef<HTMLSpanElement | null>(null);
    const tooltipRef = useRef<HTMLDivElement | null>(null);
    const [open, setOpen] = useState(false);
    const [position, setPosition] = useState<TooltipPosition | null>(null);
    const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

    const updatePosition = useCallback(() => {
        const trigger = triggerRef.current;
        if (!trigger) return;

        const rect = trigger.getBoundingClientRect();
        const tooltipHeight = tooltipRef.current?.offsetHeight ?? 56;
        const centerX = rect.left + rect.width / 2;
        const minLeft = viewportPadding + tooltipMaxWidth / 2;
        const maxLeft = window.innerWidth - viewportPadding - tooltipMaxWidth / 2;
        const left = Math.min(Math.max(centerX, minLeft), Math.max(minLeft, maxLeft));
        const canShowAbove = rect.top - tooltipGap - tooltipHeight > viewportPadding;
        const top = canShowAbove
            ? rect.top - tooltipGap - tooltipHeight
            : rect.bottom + tooltipGap;

        setPosition({
            left,
            top: Math.max(viewportPadding, top),
            transform: "translateX(-50%)",
        });
    }, []);

    useLayoutEffect(() => {
        if (open) {
            updatePosition();
        }
    }, [open, updatePosition]);

    useEffect(() => {
        if (!open) return;

        const handleUpdate = () => updatePosition();
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };

        window.addEventListener("resize", handleUpdate);
        window.addEventListener("scroll", handleUpdate, true);
        window.addEventListener("keydown", handleKeyDown);

        return () => {
            window.removeEventListener("resize", handleUpdate);
            window.removeEventListener("scroll", handleUpdate, true);
            window.removeEventListener("keydown", handleKeyDown);
        };
    }, [open, updatePosition]);

    const show = () => {
        setPortalContainer(getTooltipContainer(triggerRef.current));
        setOpen(true);
    };

    const hide = () => {
        setOpen(false);
    };

    const handleClick = () => {
        if (!toggleOnClick) return;
        setPortalContainer(getTooltipContainer(triggerRef.current));
        setOpen(true);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLSpanElement>) => {
        if (!toggleOnClick || (event.key !== "Enter" && event.key !== " ")) return;
        event.preventDefault();
        handleClick();
    };

    const tooltip = open && portalContainer
        ? createPortal(
            <div
                id={tooltipId}
                ref={tooltipRef}
                role="tooltip"
                style={{
                    background: "#303030",
                    borderRadius: 6,
                    boxShadow: "0 8px 24px rgba(0, 0, 0, 0.18)",
                    color: "white",
                    fontSize: 13,
                    left: position?.left ?? 0,
                    lineHeight: "18px",
                    maxWidth: tooltipMaxWidth,
                    opacity: position ? 1 : 0,
                    padding: "8px 10px",
                    pointerEvents: "none",
                    position: "fixed",
                    textAlign: "left",
                    top: position?.top ?? 0,
                    transform: position?.transform,
                    transition: "opacity 80ms ease",
                    whiteSpace: "normal",
                    zIndex: tooltipZIndex,
                }}
            >
                {content}
            </div>,
            portalContainer
        )
        : null;

    return (
        <span
            aria-describedby={open ? tooltipId : undefined}
            onBlur={hide}
            onClick={handleClick}
            onFocus={show}
            onKeyDown={handleKeyDown}
            onMouseEnter={show}
            onMouseLeave={hide}
            ref={triggerRef}
            style={triggerStyle}
        >
            {children}
            {tooltip}
        </span>
    );
};

export const SettingsHelpTooltip = ({ content }: { content: string }) => (
    <SettingsTooltip content={content} toggleOnClick>
        <span aria-label={content} role="img" style={helpIconStyle} tabIndex={0}>
            ?
        </span>
    </SettingsTooltip>
);
